import React, { useRef } from 'react';
import Plot from 'react-plotly.js';
import { Alert } from 'react-bootstrap';
import PropTypes from 'prop-types';
import Plotly from 'plotly.js/dist/plotly';

const Pie2LevelsChartComponent = ({ data = [], dataNamesLabel, dataValuesLabel, dataParentsLabel,
    isDataAvailable, legendNewlabels, width = '100%', height = '100%' }) => {

    const insertLineBreak = (label) => {
        if (typeof label !== "string") {
            label = label.toString();
        }
        if (label.length <= 25) return label;

        const middle = Math.floor(label.length / 2);
        const spaceBefore = label.lastIndexOf(' ', middle);
        const spaceAfter = label.indexOf(' ', middle);

        let breakIndex;

        // Find the space closest to the centre of the string
        if (spaceBefore === -1 && spaceAfter === -1) {
            breakIndex = middle;
        } else if (spaceBefore === -1) {
            breakIndex = spaceAfter;
        } else if (spaceAfter === -1) {
            breakIndex = spaceBefore;
        } else {
            breakIndex = (middle - spaceBefore < spaceAfter - middle) ? spaceBefore : spaceAfter;
        }

        return label.slice(0, breakIndex) + "<br>" + label.slice(breakIndex + 1);
    }

    const ROOT = " ";
    const parentLabels = data.map(item => item[dataParentsLabel]);
    const uniqueParentLabels = [...new Set(parentLabels)];
    let parentsValuesDict = {};
    uniqueParentLabels.forEach(label => {
        parentsValuesDict[label] = data
            ?.filter(item => item[dataParentsLabel] === label)
            ?.reduce((acc, current) => acc + current[dataValuesLabel], 0)
    });
    const parentsValues = Object.values(parentsValuesDict);
    const rootValue = parentsValues?.reduce((acc, current) => acc + current, 0)
    const childrenValues = data.map((item) => item[dataValuesLabel]);
    const values = [rootValue, ...parentsValues, ...childrenValues];
    const legends = data.map(item => insertLineBreak(legendNewlabels ? legendNewlabels[item[dataNamesLabel]] : item[dataNamesLabel]));
    const labels = ["", ...uniqueParentLabels, ...legends];
    const parents = [
        "",
        // parentLabels have no parent
        ...new Array(uniqueParentLabels.length).fill(ROOT),
        // parents of app_version are parentLabels
        ...parentLabels
    ];
    const totalValue = childrenValues.reduce((acc, curr) => acc + curr, 0);

    const graphRef = useRef(null);

    const parentHover = "<b>%{label}</b><br>" +
        "Valeur: %{value}<br>" +
        "Pourcentage: %{percentRoot:.1%}";

    const childHover = "<b>%{label}</b><br>" +
        "Valeur: %{value}<br>" +
        "Pourcentage total: %{percentRoot:.1%}<br>" +
        "Pourcentage parent: %{percentParent:.1%}";

    const hovertemplate = labels.map((label, i) => {
        if (label === ROOT) {
            return "";
        }

        return parents[i] === ROOT
            ? parentHover
            : childHover;
    });

    const colorValues = [
        "#636EFA",
        "#EF553B",
        "#00CC96",
        "#AB63FA",
        "#FFA15A",
        "#19D3F3",
        "#FF6692",
        "#B6E880",
    ];
    const colorMap = Object.fromEntries(
        uniqueParentLabels.map((label, i) => [
            label,
            colorValues[i % colorValues.length]
        ])
    );
    const parentColors = uniqueParentLabels.map(label => colorMap[label]);
    const childColors = data.map(item => colorMap[item[dataParentsLabel]]);
    const colors = [
        "rgba(0,0,0,0)",
        ...parentColors,
        ...childColors
    ];
    const legendItems = <div className='d-flex'>{
        uniqueParentLabels.map(label => (
            <div key={label} style={{ display: "flex", alignItems: "center", marginLeft: 5 }}>
                <span
                    style={{
                        width: 12,
                        height: 12,
                        backgroundColor: colorMap[label],
                        marginRight: 8
                    }}
                />
                {insertLineBreak(label)}
            </div>
        ))
    }</div>;

    const handleClick = (event) => {
        const point = event.points[0];
        if (!point) return;

        // Only if click on parent
        if (point.parent === " " || point.parent === ROOT) {
            if (graphRef.current?.el && graphRef.current.el.layout.annotations[0].text == "") {
                Plotly.relayout(graphRef.current.el, {
                    'annotations[0].text': `${totalValue}`,
                });
            } else if (graphRef.current?.el) {
                Plotly.relayout(graphRef.current.el, {
                    'annotations[0].text': ``,
                });
            }
        }
    };

    return isDataAvailable
        ? data?.length > 0
            ? <div>
                {legendItems}
                <div style={{ width, height, zIndex: "1", minHeight: '30em' }}>
                    <Plot
                        ref={graphRef}
                        data={[
                            {
                                values,
                                labels,
                                parents,
                                type: 'sunburst',
                                hole: 0.5,
                                textinfo: 'label',
                                marker: { colors },
                                hovertemplate,
                                hoverlabel: {
                                    bgcolor: 'white',
                                    bordercolor: 'black',
                                },
                                branchvalues: "total",
                                maxdepth: 3,
                            }
                        ]}
                        style={{ width, height }}
                        layout={{
                            autosize: false,
                            annotations: [
                                {
                                    font: {
                                        size: 20,
                                        color: '#000',
                                    },
                                    showarrow: false,
                                    text: `${totalValue}`,
                                    x: 0.5,
                                    y: 0.5,
                                },
                            ],
                        }}
                        onClick={handleClick}
                    />
                </div>
            </div>
            : <Alert variant="warning">Aucune donnée à afficher</Alert>
        : <Alert variant="danger">Donnée non disponible</Alert>;
}

Pie2LevelsChartComponent.propTypes = {
    data: PropTypes.array.isRequired,
    dataNamesLabel: PropTypes.string.isRequired,
    dataValuesLabel: PropTypes.string.isRequired,
    dataParentsLabel: PropTypes.string.isRequired,
    isDataAvailable: PropTypes.bool.isRequired,
    legendNewlabels: PropTypes.object,
    width: PropTypes.string,
    height: PropTypes.string,
};

export default Pie2LevelsChartComponent;